/**
 * ConstructionSnapController — the construction-draw SNAP TIERS
 * (issue #839, epic #828) extracted from ConstructionTool. This is the
 * shared foundation every other construction controller already snaps
 * through: a tool OWNS a controller instance and DRIVES it by
 * composition (it is NOT a base class), mirroring the
 * `RotationController` / `ResizeController` / `OffsetController`
 * pattern. The controller owns the live/draft snap STATE (`liveSnap`,
 * `draftSnaps`, `draftHoverSnap`) plus every snap-resolver tier, while
 * the four collaborators it needs (scope / scene / project / settings)
 * are injected through the `SnapHost` interface.
 *
 * Two clusters live here:
 *  - **Live/draft snap state + overlay** — `snapRadiusMm`,
 *    `updateLiveSnap` / `clearLiveSnap` / `refreshLiveSnapOverlay`
 *    (the drag-time source↔candidate coincident-snap pair) and
 *    `findDraftSnap` / `recordDraftSnap` / `updateDraftHoverSnap` /
 *    `clearDraftSnaps` (the draw-time anchor snaps carried into the
 *    on-commit constraint pass).
 *  - **Positional tier resolvers** — `shouldSnapAngle/Sizes/Center`
 *    (modifier + toggle predicates), `maybeSnapToGrid` /
 *    `positionalSnapTarget` (grid #259 fallback), `findLineSnap` /
 *    `angleLineEnd` (line-crossing #323), and `updateDraftGridHover`
 *    (the free-space snap dot).
 *
 * Coupling is high (this is the last slice deliberately): the state is
 * read across the tool by ALREADY-EXTRACTED controllers via their host
 * hooks — RotationController's `RotationHost` reads
 * `getLiveSnap`/`setLiveSnap`/`clearLiveSnap`/`refreshLiveSnapOverlay`/
 * `snapRadiusMm`, and the `DrawGestureHost.resolveDraftPlacement` /
 * `resolveDraftHover` / `onEmptyDraftHover` route through the draft
 * tiers. ConstructionTool keeps those public host-hook methods and now
 * DELEGATES them here, and exposes `getDraftSnaps` / `setDraftSnaps` /
 * `commitHoverSnap` for the few places that touched the draft-snap
 * arrays directly. Behaviour is unchanged — every method body is a
 * verbatim lift.
 */
import paper from "paper";
import type { Scene } from "../canvas/Scene";
import type { ToolSettings } from "./EditorContext";
import type { PointerSample } from "../canvas/PointerInput";
import { projectConstructionLines, type Project } from "../model/Project";
import {
  findNearestSnapCandidate,
  type SnapRefLite,
} from "../model/constraintEnforcement";
import {
  angleModifierHeld,
  isAngleSnapActive,
  isCenterSnapActive,
  isGridSnapActive,
  isLineSnapActive,
  isPointSnapActive,
  isSizesSnapActive,
  snapPointToGrid,
  snapToCommonAngle,
  type AngleSnapResult,
} from "./dragSnap";
import { nearestAngleReference, type AngleReference } from "./angleReferences";
import { isFeatureEnabled } from "../featureFlags";
import { drawSnapRadiusMm } from "./drawSnap";
import {
  edgeIntersections,
  nearestIntersection,
} from "../model/edgeIntersections";

/** The per-frame (source ↔ candidate) snap pair. Structurally
 *  identical to RotationController's `RotationLiveSnap` so reads /
 *  writes stay in lock-step across the RotationHost boundary. */
export interface LiveSnapPair {
  source: SnapRefLite;
  candidate: SnapRefLite;
  candidatePos: { x: number; y: number };
}

/** A draw-time snap record — one per committed draft vertex that
 *  landed on an existing line's snap target. On commit each is matched
 *  back to the new line's segment by position and turned into a
 *  coincident constraint. */
export interface DraftSnapRecord {
  pos: { x: number; y: number };
  candidate: SnapRefLite;
}

/** Everything the ConstructionSnapController needs from its owning
 *  tool: the four core collaborators. All snap STATE lives in the
 *  controller. */
export interface SnapHost {
  getScope(): paper.PaperScope;
  getScene(): Scene;
  getProject(): Project;
  getSettings(): ToolSettings;
}

export class ConstructionSnapController {
  /** Drag-time snap-candidate state. Tracks the (source ↔ candidate)
   *  snap pair the user is about to coincident-pin when they
   *  release the current drag. Both source and candidate can be
   *  anchor OR primitive bbox centre: a vertex drag's source is
   *  always an anchor, but a whole-line drag of a primitive also
   *  offers the line's centre as a source so centre-to-centre
   *  snapping works. Set per-frame from `findNearestSnapCandidate`;
   *  cleared when the cursor leaves the snap radius or drag ends. */
  private liveSnap: LiveSnapPair | null = null;

  /** Draw-time snap records — one per *committed* draft vertex that
   *  landed on an existing line's snap target (anchor or primitive
   *  centre). On commit, each record is matched back to the new
   *  line's segment by position and turned into a coincident
   *  constraint, with the matched ref kind preserved. Cleared on
   *  cancel / mode switch / successful commit. */
  private draftSnaps: DraftSnapRecord[] = [];

  /** The snap candidate the current draft cursor is hovering over
   *  (rubber-band / mouse-move). Drives the yellow highlight ring;
   *  doesn't lock anything in until the user clicks / releases at
   *  which point it gets pushed into `draftSnaps`. */
  private draftHoverSnap: SnapRefLite | null = null;

  constructor(private host: SnapHost) {}

  // -------------------------------------------------------------------
  // State accessors (for the few sites that touched the fields directly)
  // -------------------------------------------------------------------

  /** RotationHost hook: the live drag-snap pair, or null. */
  getLiveSnap(): LiveSnapPair | null {
    return this.liveSnap;
  }

  /** RotationHost hook: set the live drag-snap pair (used by the
   *  rotation controller's own snap refresh). Does NOT touch the scene
   *  overlay — `refreshLiveSnapOverlay` renders it. */
  setLiveSnap(snap: LiveSnapPair | null): void {
    this.liveSnap = snap;
  }

  /** Snapshot of the pending draft snaps (used by the poly commit to
   *  carry them past a `cancelDraft` reset, and by `commitPathData`
   *  before the reducer runs). */
  getDraftSnaps(): DraftSnapRecord[] {
    return this.draftSnaps.slice();
  }

  /** Restore a previously-snapshotted draft-snaps array (poly commit
   *  re-seeds the snaps `cancelDraft` wiped so `commitPathData` can
   *  match them to the new line's anchors). */
  setDraftSnaps(snaps: DraftSnapRecord[]): void {
    this.draftSnaps = snaps;
  }

  /** Commit the current hover snap as a draft record at `pos` (the
   *  drag-style commit's end-point snap). No-op when nothing's
   *  hovered. Clears the hover ring. */
  commitHoverSnap(pos: { x: number; y: number }): void {
    if (!this.draftHoverSnap) return;
    this.draftSnaps.push({
      pos: { x: pos.x, y: pos.y },
      candidate: this.draftHoverSnap,
    });
    this.draftHoverSnap = null;
    this.host.getScene().setConstructionSnapCandidateHover(null);
  }

  // -------------------------------------------------------------------
  // Live/draft snap state + overlay
  // -------------------------------------------------------------------

  /** Compute the project-mm distance corresponding to the snap
   *  radius in screen pixels. Inverse-zoom so the snap zone always
   *  feels the same size regardless of view zoom. */
  snapRadiusMm(): number {
    return drawSnapRadiusMm(this.host.getScope());
  }

  /** Find the nearest snap-candidate anchor for the current drag
   *  source. Updates `this.liveSnap` and the scene-side highlight.
   *  Returns the candidate's snapped project position (where the
   *  source anchor *would* land if the user released right now), or
   *  null when nothing's in range — caller uses the original cursor
   *  position in that case. Skipped when the constraints feature
   *  flag is off so the no-constraints code path is identical to
   *  before. */
  updateLiveSnap(
    sources: Array<{
      ref: SnapRefLite;
      pos: { x: number; y: number };
    }>,
    extraExcludedLineIds?: ReadonlySet<string>,
  ): {
    sourceRef: SnapRefLite;
    candidatePos: { x: number; y: number };
  } | null {
    if (!isPointSnapActive(this.host.getSettings())) {
      this.clearLiveSnap();
      return null;
    }
    const scope = this.host.getScope();
    const project = this.host.getProject();
    const lines = projectConstructionLines(project);
    const hit = findNearestSnapCandidate(
      lines,
      sources,
      this.snapRadiusMm(),
      scope,
      extraExcludedLineIds,
    );
    if (!hit) {
      this.clearLiveSnap();
      return null;
    }
    this.liveSnap = {
      source: hit.sourceRef,
      candidate: hit.candidateRef,
      candidatePos: hit.candidatePos,
    };
    // Don't render the hover ring here — the drag site calls
    // `refreshLiveSnapOverlay` at the end of the frame, after the
    // closure-translate pass, so the rings sit on post-snap
    // positions instead of lingering at last frame's spots.
    return { sourceRef: hit.sourceRef, candidatePos: hit.candidatePos };
  }

  clearLiveSnap(): void {
    if (this.liveSnap === null) return;
    this.liveSnap = null;
    this.host.getScene().setConstructionSnapCandidateHover(null);
  }

  /** Re-render the snap-candidate hover overlay from the current
   *  `liveSnap` state, reading live segment positions on the scene.
   *  Called at the end of each drag frame, after the closure
   *  translate pass, so the source and candidate rings reflect
   *  post-drag-frame geometry. */
  refreshLiveSnapOverlay(): void {
    if (this.liveSnap) {
      this.host.getScene().setConstructionSnapCandidateHover({
        source: this.liveSnap.source,
        candidate: this.liveSnap.candidate,
      });
    } else {
      this.host.getScene().setConstructionSnapCandidateHover(null);
    }
  }

  /** Find an existing-line anchor within snap range of the draft
   *  cursor. Returns the candidate ref + its project position, or
   *  null when nothing's close enough. Skipped when the constraints
   *  feature flag is off so the no-constraints code path is
   *  identical to before. Unlike `updateLiveSnap`, the source has
   *  no on-scene anchor (the draft line doesn't exist yet) — every
   *  existing-line anchor is a candidate. */
  // Returning null here also clears any prior hover ring via the
  // `updateDraftHoverSnap(null)` path the callers take, so toggling
  // off point snap mid-draft makes the highlight disappear without
  // any extra plumbing.
  findDraftSnap(p: {
    x: number;
    y: number;
  }): { ref: SnapRefLite; pos: { x: number; y: number } } | null {
    if (!isPointSnapActive(this.host.getSettings())) return null;
    const scope = this.host.getScope();
    const lines = projectConstructionLines(this.host.getProject());
    const hit = findNearestSnapCandidate(
      lines,
      // Synthetic source ref — `findNearestSnapCandidate` excludes
      // anchors on the source's `constructionId`; a non-existent
      // id means every existing-line anchor (and bbox centre) is a
      // candidate.
      [
        {
          ref: {
            kind: "anchor",
            constructionId: "__draft__",
            pathIndex: 0,
            segmentIndex: 0,
          },
          pos: p,
        },
      ],
      this.snapRadiusMm(),
      scope,
    );
    if (!hit) return null;
    return { ref: hit.candidateRef, pos: hit.candidatePos };
  }

  /** Record a draft-time snap: the user just clicked / released the
   *  cursor while it was hovering an existing anchor. The position
   *  goes into `draftSnaps` for the on-commit constraint pass.
   *  Skipped silently when the cursor isn't snapped — caller passes
   *  null and we still clear the hover ring. */
  recordDraftSnap(
    snap: { ref: SnapRefLite; pos: { x: number; y: number } } | null,
  ): void {
    if (snap) {
      this.draftSnaps.push({ pos: snap.pos, candidate: snap.ref });
    }
    // Clear the rubber-band hover ring — the next move event will
    // re-arm it if the cursor is still in range of an anchor.
    this.draftHoverSnap = null;
    this.host.getScene().setConstructionSnapCandidateHover(null);
  }

  /** Update the draft-hover snap highlight from the current cursor
   *  position. Used by `onMove` (drag-style shapes) and `onHover`
   *  (polyline / polygon rubber-band) to keep the yellow ring on
   *  the about-to-snap anchor in real time. */
  updateDraftHoverSnap(
    snap: { ref: SnapRefLite; pos: { x: number; y: number } } | null,
  ): void {
    if (snap) {
      this.draftHoverSnap = snap.ref;
      this.host.getScene().setConstructionSnapCandidateHover({
        candidate: snap.ref,
      });
    } else if (this.draftHoverSnap) {
      this.draftHoverSnap = null;
      this.host.getScene().setConstructionSnapCandidateHover(null);
    }
  }

  clearDraftSnaps(): void {
    if (this.draftSnaps.length > 0) this.draftSnaps = [];
    if (this.draftHoverSnap !== null) {
      this.draftHoverSnap = null;
      this.host.getScene().setConstructionSnapCandidateHover(null);
    }
    // Free-floating grid hover dot lives on the same "preview the
    // next click" axis as the anchor hover ring; clear them in
    // lockstep so a poly commit / sub-mode switch leaves a clean
    // canvas.
    this.host.getScene().setConstructionDraftSnapPoint(null);
  }

  // -------------------------------------------------------------------
  // Positional tier resolvers
  // -------------------------------------------------------------------

  shouldSnapAngle(s: PointerSample): boolean {
    return isAngleSnapActive(this.host.getSettings(), angleModifierHeld(s));
  }
  shouldSnapSizes(s: PointerSample): boolean {
    return isSizesSnapActive(this.host.getSettings(), s.shiftKey);
  }
  shouldSnapCenter(s: PointerSample): boolean {
    return isCenterSnapActive(this.host.getSettings(), s.altKey);
  }

  /**
   * The nearest visible construction edge to `origin`, as an angle reference
   * (#162), or null when nothing is within the snap radius.
   *
   * `excludeId` drops the line being drawn or edited, so a segment can't take
   * its angles from the very geometry it is changing.
   */
  angleReferenceAt(
    origin: { x: number; y: number },
    excludeId?: string,
  ): AngleReference | null {
    if (!isFeatureEnabled("angle-to-line-snap")) return null;
    const edges = projectConstructionLines(this.host.getProject())
      .filter((l) => l.visible !== false && l.id !== excludeId)
      .map((l) => l.pathData);
    if (edges.length === 0) return null;
    return nearestAngleReference(
      origin,
      edges,
      this.snapRadiusMm(),
      this.host.getScope(),
    );
  }

  /**
   * Angle snap for a segment running `origin` → `cursor`, with the nearby-line
   * reference (#162) mixed into the candidate set. Returns the snap plus the
   * reference it actually used, so the caller can draw it as a ghost arm.
   *
   * The reference is resolved from `origin`, which is fixed for the whole
   * drag — resolving it from the moving cursor would swap the lattice under
   * the maker's hand mid-stroke.
   */
  snapAngle(
    origin: { x: number; y: number },
    cursor: { x: number; y: number },
    excludeId?: string,
  ): AngleSnapResult & { reference: AngleReference | null } {
    const reference = this.angleReferenceAt(origin, excludeId);
    const snapped = snapToCommonAngle(
      origin,
      cursor,
      reference?.dirRad ?? null,
    );
    return {
      ...snapped,
      reference: snapped.refRad != null ? reference : null,
    };
  }

  /** Lowest-priority draft snap (issue #259): when grid snap is on
   *  AND no anchor / self / angle / size snap fired, round the
   *  proposed point onto the cutting-mat grid. Returns the input
   *  unchanged when grid snap is off or the mat has no grid info.
   *  Same pipeline `SelectTool` / `groupDrag` already use during
   *  moves — extending it to the Construction draw entry points
   *  closes the gap the user hit. */
  maybeSnapToGrid<P extends { x: number; y: number }>(pt: P): P {
    const t = this.positionalSnapTarget(pt);
    return t ? { ...pt, x: t.x, y: t.y } : pt;
  }

  /** The free-space positional snap target for `p` — the nearest point along
   *  any existing line when Line snap is active (#323), else the nearest mat
   *  grid intersection when Grid snap is active, else null. Line wins over grid
   *  (a deliberate "land on this edge" beats the lattice). Anchor / point snap
   *  is resolved separately by the caller and takes priority over both. Kept
   *  distinct from `maybeSnapToGrid` so the hover renderer can show / hide the
   *  preview dot based on whether a snap would actually fire. */
  positionalSnapTarget(p: {
    x: number;
    y: number;
  }): { x: number; y: number } | null {
    // Where two lines CROSS (#128) is a more specific answer than "somewhere
    // along one of them", so it's checked first — the same reason an anchor
    // beats a line projection. It rides the Line-snap toggle because a crossing
    // exists only because lines do, matching `angleLineEnd`'s treatment of a
    // ray x line crossing. No constraint is recorded: the point lands there and
    // stays put, exactly like the grid.
    const cross = this.findIntersectionSnap(p);
    if (cross) return cross;
    const line = this.findLineSnap(p);
    if (line) return line;
    if (!isGridSnapActive(this.host.getSettings())) return null;
    const grid = this.host.getScene().getMatGridInfo();
    if (!grid) return null;
    return snapPointToGrid(p, grid);
  }

  /**
   * Nearest crossing of two visible construction lines within the snap radius
   * (#128), or null.
   *
   * Crossings are recomputed per call rather than cached: unlike the leather
   * draw tools, which gather once per gesture against static geometry, a
   * construction drag CHANGES the lines as it goes, so a cached set would go
   * stale mid-gesture — and the bounds cull inside `edgeIntersections` keeps
   * the pairwise scan cheap enough for the handful of lines a drawing has.
   *
   * `exclude` drops the line(s) being edited, so a vertex can't chase a
   * crossing that only exists because of where it currently is.
   */
  findIntersectionSnap(
    p: { x: number; y: number },
    exclude?: string | ReadonlySet<string>,
  ): { x: number; y: number } | null {
    if (!isLineSnapActive(this.host.getSettings())) return null;
    const edges = projectConstructionLines(this.host.getProject())
      .filter((l) => l.visible !== false && !isExcluded(l.id, exclude))
      .map((l) => l.pathData);
    if (edges.length < 2) return null;
    return nearestIntersection(
      p,
      edgeIntersections(edges, this.host.getScope()),
      this.snapRadiusMm(),
    );
  }

  /** Nearest point ON any visible construction line within the snap radius, or
   *  null. Line snap (#323) lets a freshly-drawn vertex land exactly on an
   *  existing edge, not just its anchors. `exclude` drops the line(s) being
   *  edited so a vertex never snaps to itself. */
  findLineSnap(
    p: { x: number; y: number },
    exclude?: string | ReadonlySet<string>,
  ): { x: number; y: number } | null {
    if (!isLineSnapActive(this.host.getSettings())) return null;
    const scope = this.host.getScope();
    const cur = new scope.Point(p.x, p.y);
    const threshold = this.snapRadiusMm();
    let best: { x: number; y: number } | null = null;
    let bestDist = threshold;
    for (const line of projectConstructionLines(this.host.getProject())) {
      if (line.visible === false || isExcluded(line.id, exclude)) continue;
      let cp: paper.CompoundPath;
      try {
        cp = new scope.CompoundPath({ pathData: line.pathData, insert: false });
      } catch {
        continue;
      }
      const subs =
        cp.children && cp.children.length > 0
          ? (cp.children as paper.Path[])
          : [cp as unknown as paper.Path];
      for (const sub of subs) {
        const loc = sub.getNearestLocation(cur);
        if (!loc) continue;
        const d = loc.point.getDistance(cur);
        if (d <= bestDist) {
          bestDist = d;
          best = { x: loc.point.x, y: loc.point.y };
        }
      }
      cp.remove();
    }
    return best;
  }

  /** Line snap composed with an angle snap (#323): the drawn segment stays on
   *  the ray from `origin` through `anglePt`, and its END snaps onto the point
   *  where that ray crosses the nearest visible line — engaging as the END
   *  reaches the crossing (within the snap radius). Returns the crossing (also
   *  the indicator position, so the dot sits on the line at the line's end), or
   *  null when line snap is off, the ray is degenerate, or no crossing is in
   *  range — the caller then keeps the pure angle point. */
  angleLineEnd(
    origin: { x: number; y: number },
    anglePt: { x: number; y: number },
  ): { x: number; y: number } | null {
    if (!isLineSnapActive(this.host.getSettings())) return null;
    const rdx = anglePt.x - origin.x;
    const rdy = anglePt.y - origin.y;
    const rlen = Math.hypot(rdx, rdy);
    if (rlen < 1e-6) return null;
    const scope = this.host.getScope();
    const ux = rdx / rlen;
    const uy = rdy / rlen;
    const BIG = 100000; // mm — well past any cutting mat, both directions
    let ray: paper.Path;
    try {
      ray = new scope.Path.Line({
        from: new scope.Point(origin.x - ux * BIG, origin.y - uy * BIG),
        to: new scope.Point(origin.x + ux * BIG, origin.y + uy * BIG),
        insert: false,
      });
    } catch {
      return null;
    }
    const end = new scope.Point(anglePt.x, anglePt.y);
    let best: { x: number; y: number } | null = null;
    let bestDist = this.snapRadiusMm();
    for (const line of projectConstructionLines(this.host.getProject())) {
      if (line.visible === false) continue;
      let cp: paper.CompoundPath;
      try {
        cp = new scope.CompoundPath({ pathData: line.pathData, insert: false });
      } catch {
        continue;
      }
      let inters: paper.CurveLocation[] = [];
      try {
        inters = ray.getIntersections(cp);
      } catch {
        cp.remove();
        continue;
      }
      for (const it of inters) {
        const d = it.point.getDistance(end);
        if (d <= bestDist) {
          bestDist = d;
          best = { x: it.point.x, y: it.point.y };
        }
      }
      cp.remove();
    }
    ray.remove();
    return best;
  }

  /** Snap a dragged HANDLE — a free cursor point that isn't an anchor of any
   *  line, such as a resize handle (#1169) — against every enabled snap, in
   *  the order a vertex drag uses: Point (another line's anchor, segment
   *  midpoint, center or edge midpoint), then a crossing of two lines, then
   *  a point along a line, then the mat grid. `excluded` drops the lines the
   *  gesture is moving (the edited line and anything following it), whose
   *  model geometry is stale mid-drag.
   *
   *  Shows the matching indicator — the candidate ring for a point snap, the
   *  free dot otherwise — and returns where the handle lands plus, for a
   *  point snap, the candidate it landed on. Null (and no indicator) when
   *  nothing is in range. `clearHandleDragSnap` removes the indicator. */
  snapHandleDrag(
    p: { x: number; y: number },
    excluded: ReadonlySet<string>,
  ): {
    point: { x: number; y: number };
    candidate: SnapRefLite | null;
  } | null {
    const scene = this.host.getScene();
    if (isPointSnapActive(this.host.getSettings())) {
      const hit = findNearestSnapCandidate(
        projectConstructionLines(this.host.getProject()),
        // Synthetic source — the handle has no anchor of its own, so no
        // candidate is dropped for sharing its line (the real lines are in
        // `excluded`).
        [
          {
            ref: {
              kind: "anchor",
              constructionId: "__handle__",
              pathIndex: 0,
              segmentIndex: 0,
            },
            pos: p,
          },
        ],
        this.snapRadiusMm(),
        this.host.getScope(),
        excluded,
      );
      if (hit) {
        scene.setConstructionDraftSnapPoint(null);
        scene.setConstructionSnapCandidateHover({
          candidate: hit.candidateRef,
        });
        return { point: hit.candidatePos, candidate: hit.candidateRef };
      }
    }
    scene.setConstructionSnapCandidateHover(null);
    let point =
      this.findIntersectionSnap(p, excluded) ?? this.findLineSnap(p, excluded);
    if (!point && isGridSnapActive(this.host.getSettings())) {
      const grid = scene.getMatGridInfo();
      if (grid) point = snapPointToGrid(p, grid);
    }
    scene.setConstructionDraftSnapPoint(point);
    return point ? { point, candidate: null } : null;
  }

  /** Remove the indicator `snapHandleDrag` shows. */
  clearHandleDragSnap(): void {
    const scene = this.host.getScene();
    scene.setConstructionSnapCandidateHover(null);
    scene.setConstructionDraftSnapPoint(null);
  }

  /** Show or hide the free-floating yellow snap-target dot that
   *  previews where a Construction Draw click would land when grid or
   *  line snap (#323) fires on empty space. Mutually exclusive with the
   *  anchor-hover ring — only one snap indicator at a time. */
  updateDraftGridHover(
    p: { x: number; y: number },
    anchorSnapActive: boolean,
  ): void {
    if (anchorSnapActive) {
      this.host.getScene().setConstructionDraftSnapPoint(null);
      return;
    }
    const target = this.positionalSnapTarget(p);
    this.host.getScene().setConstructionDraftSnapPoint(target);
  }
}

/** True when `id` is the excluded id, or one of the excluded set. */
function isExcluded(
  id: string,
  exclude: string | ReadonlySet<string> | undefined,
): boolean {
  if (exclude === undefined) return false;
  return typeof exclude === "string" ? id === exclude : exclude.has(id);
}
